import type { ServiceSignUpResult, SignUpProps } from '../../types';
import type { IdentityProvider } from '../identity-provider';
import type { HttpClient } from '../client-app/index';
import { cognitoSignIn } from './sign-in';
import { translateIdpError, IdpErrorName } from './errors';
import { toE164 } from './phone-utils';
import { SignUpStatus, SignUpStep } from '../../constants';
import type { AuthLogger } from '../../shared/auth-logger';

export function createSignUpFlow(
  idp: IdentityProvider,
  http: HttpClient,
  t: (key: string, opts?: { defaultValue: string }) => string,
  logger?: AuthLogger,
) {
  let pendingProps: SignUpProps | null = null;

  return {
    async signUp(props: SignUpProps): Promise<ServiceSignUpResult> {
      pendingProps = props;
      try {
        logger?.log(SignUpStep.Attempt, { email: props.email.replace(/(.{2}).*(@.*)/, '$1***$2') });
        const output = await idp.signUp(props.email, props.password, {
          email: props.email,
          givenName: props.firstName,
          familyName: props.lastName,
          phoneNumber: props.homePhone ? toE164(props.homePhone) : undefined,
        });

        if (!output.complete) {
          return { status: SignUpStatus.RequiresEmailVerification };
        }

        const result = await cognitoSignIn(idp, http, props.email, props.password, {
          phone: props.homePhone,
          profile: { firstName: props.firstName, lastName: props.lastName },
        }, t, logger);
        if (result.user) return { status: SignUpStatus.Confirmed, user: result.user };
        return { status: SignUpStatus.Incomplete };
      } catch (error) {
        const name = (error as { name?: string } | null)?.name;
        if (name === IdpErrorName.UsernameExists) {
          try {
            const result = await cognitoSignIn(idp, http, props.email, props.password, {
              phone: props.homePhone,
              profile: { firstName: props.firstName, lastName: props.lastName },
            }, t, logger);
            if (result.user) return { status: SignUpStatus.Confirmed, user: result.user };
            return { status: SignUpStatus.Incomplete };
          } catch (signInError) {
            const signInName = (signInError as { name?: string } | null)?.name;
            if (signInName === IdpErrorName.UserNotConfirmed) {
              idp.resendSignUpCode(props.email).catch(err => {
                logger?.log(SignUpStep.ResendCodeError, { error: err instanceof Error ? err.message : String(err) });
              });
              return { status: SignUpStatus.RequiresEmailVerification };
            }
            throw signInError;
          }
        }
        translateIdpError(error, t);
      }
    },

    async confirmSignUp(email: string, code: string): Promise<ServiceSignUpResult> {
      try {
        logger?.log(SignUpStep.Confirm, { email: email.replace(/(.{2}).*(@.*)/, '$1***$2') });
        const output = await idp.confirmSignUp(email, code);
        if (!output.complete) return { status: SignUpStatus.RequiresEmailVerification };

        const props = pendingProps;
        if (props?.password) {
          const result = await cognitoSignIn(idp, http, email, props.password, {
            phone: props.homePhone,
            profile: { firstName: props.firstName, lastName: props.lastName },
          }, t, logger);
          if (result.user) {
            pendingProps = null;
            return { status: SignUpStatus.Confirmed, user: result.user };
          }
        }
        pendingProps = null;
        return { status: SignUpStatus.Incomplete };
      } catch (error) {
        translateIdpError(error, t);
      }
    },

    async resendSignUpCode(email: string): Promise<void> {
      try {
        await idp.resendSignUpCode(email);
      } catch (error) {
        translateIdpError(error, t);
      }
    },
  };
}
